import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setAllItems } from '../../../../../store/slices/imageOverlaySlice';
import { saveImageItems, loadImageItems } from '../Drawing/drawingService';
import { setSaveStatus } from '../../../../../store/slices/navigationSlice';
import { useProjectHeader } from '../../../../../pages/buildProject/Helpers/pageDiv/ProjectHeaderContext';

const IMG_LS_KEY    = (floorId) => `image-overlay-floor-${floorId}`;
const IMG_MAX_RETRY = 1;
const IMG_RETRY_MS  = 2000;

const stripSelected = (items) => items.map(({ selected, ...rest }) => rest);

export default function ImageAutoSave() {
  const dispatch     = useDispatch();
  const items        = useSelector((s) => s.imageOverlay.items);
  const currentFloor = useSelector((s) => s.api.currentFloor);
  const projectData  = useSelector((s) => s.api.projectData);
  const floorId      = currentFloor?.enc_id ?? null;
  const projectId    = projectData?.enc_id ?? null;

  const { handleEnableDisable } = useProjectHeader();

  const baselineRef = useRef(null);
  const saveTimer   = useRef(null);
  const saveVersion = useRef(0);

  const attemptSave = (targetFloorId, toSave, serialized, version, attempt = 0) => {
    if (version !== saveVersion.current) return;

    saveImageItems(targetFloorId, toSave)
      .then((result) => {
        if (version !== saveVersion.current) return;
        if (result.type === 1) {
          baselineRef.current = serialized;
          dispatch(setSaveStatus('saved'));
          setTimeout(() => dispatch(setSaveStatus('idle')), 3000); 
          handleEnableDisable()
        } else {
          dispatch(setSaveStatus('failed'));
          throw new Error(result.errormessage || 'Save failed');
        }
      })
      .catch(() => {
        if (version !== saveVersion.current) return;
        if (attempt < IMG_MAX_RETRY - 1) {
          setTimeout(
            () => attemptSave(targetFloorId, toSave, serialized, version, attempt + 1),
            IMG_RETRY_MS * Math.pow(2, attempt),
          );
        } else {
          try { localStorage.setItem(IMG_LS_KEY(targetFloorId), JSON.stringify(toSave)); } catch {}
        }
        dispatch(setSaveStatus('failed'));
      });
  };

  useEffect(() => {
    if (!floorId) {
      baselineRef.current = null;
      return;
    }

    baselineRef.current = null;
    dispatch(setSaveStatus('idle'));

    const load = async () => {
      try {
        const saved = await loadImageItems(floorId);
        const normalized = saved?.length > 0 ? saved : [];
        baselineRef.current = JSON.stringify(stripSelected(normalized));
        dispatch(setAllItems(normalized));
      } catch {
        baselineRef.current = JSON.stringify([]);
        dispatch(setAllItems([]));
      }
    };

    load();

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [floorId, dispatch]);

  useEffect(() => {
    if (baselineRef.current === null) return;
    if (!floorId) return;

    const toSave = stripSelected(items);
    const serialized = JSON.stringify(toSave);
    if (serialized === baselineRef.current) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      saveVersion.current += 1;
      const version = saveVersion.current;
      dispatch(setSaveStatus('saving'));
      attemptSave(floorId, toSave, serialized, version);
    }, 1000);
  }, [items, floorId]);

  return null;
}
